import React, { useState, useRef, useEffect } from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { studentAddExperienceAPI } from "../../redux/features/studentDetailSlice";
import { instructorAddExperienceAPI } from "../../redux/features/intructorSlice";
import { instructorAddExperienceAPIForEditProfile } from "../../redux/features/adminSlice";
import { instructorUpdateExperienceAPIForEditProfile } from "../../redux/features/adminSlice";
import CustomButton from "../CustomButton/CustomButton";
import { skipClickAPI } from "../../redux/features/intructorSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { useSearchParams } from "react-router-dom";

// Define libraries needed for Google Maps
const libraries = ["places"];

const workTypeOptions = ["Hybrid", "On-site", "Remote"];

// PlaceAutocomplete Component
const PlaceAutocomplete = ({ index, setFieldValue, values }) => {
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && values.experience[index].location) {
      inputRef.current.value = values.experience[index].location;
    }
  }, [values.experience[index].location]);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.formatted_address) {
      setFieldValue(`experience[${index}].location`, place.formatted_address);
    } else {
      console.warn("No place selected or formatted_address not available");
    }
  };

  return (
    <div className="mb-12 mb-md-16" style={{ position: "relative" }}>
      <label
        htmlFor={`experience[${index}].location`}
        className="form-label black-100 fw-semibold mb-8 mb-lg-12"
      >
        Location
      </label>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={onPlaceChanged}
        fields={["formatted_address"]}
      >
        <Field name={`experience[${index}].location`}>
          {({ field }) => (
            <input
              {...field}
              ref={inputRef}
              type="text"
              id={`experience[${index}].location`}
              className="form-control"
              placeholder="Enter a location"
              style={{ width: "100%" }}
              onChange={(e) => {
                setFieldValue(`experience[${index}].location`, e.target.value);
              }}
            />
          )}
        </Field>
      </Autocomplete>
      <ErrorMessage
        name={`experience[${index}].location`}
        component="div"
        className="text-danger error"
      />
    </div>
  );
};

const validationSchema = Yup.object({
  experience: Yup.array()
    .of(
      Yup.object().shape({
        company_name: Yup.string()
          .min(2, "Company name must be at least 2 characters")
          .required("Company name is required"),
        role: Yup.string()
          .min(2, "Role must be at least 2 characters")
          .required("Role is required"),
        work_type: Yup.string()
          .oneOf(workTypeOptions, "Invalid work type")
          .required("Work type is required"),
        location: Yup.string()
          .min(2, "Location must be at least 2 characters")
          .max(100, "Location must be less than 100 characters")
          .required("Location is required"),
        start_date: Yup.date()
          .required("Start date is required")
          .max(new Date(), "Start date cannot be in the future")
          .nullable(),
        end_date: Yup.date().when("currently_working", {
          is: false,
          then: (schema) =>
            schema
              .required("End date is required")
              .min(Yup.ref("start_date"), "End date must be after start date"),
          otherwise: (schema) => schema.nullable(),
        }),
        currently_working: Yup.boolean(),
        skills: Yup.array()
          .of(Yup.string().min(3, "Skill must be at least 3 characters"))
          .min(1, "At least one skill is required")
          .required("Skills are required"),
        add_experience: Yup.string()
          .min(10, "About must be at least 10 characters")
          .max(250, "About must be less than 250 characters")
          .required("About yourself is required"),
      })
    )
    .min(1, "At least one experience entry is required"),
});

export default function ExperienceForm({
  title,
  handleClose,
  experience,
  refreshDataHandler,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState(null);
  const auth = useSelector((state) => state.auth);
  const today = new Date();

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {}, [experience]);

  if (loadError) {
    return <div>Error loading Google Maps API: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps API...</div>;
  }

  // Initialize initialValues based on experience prop
  const initialValues = {
    experience: experience
      ? [
          {
            id: experience.id || null,
            company_name: experience.company_name || "",
            role: experience.role || "",
            work_type: experience.work_type || "",
            location: experience.location || "",
            start_date: experience.start_date || "",
            end_date: experience.end_date || "",
            currently_working: experience.currently_working || false,
            skills: experience.skills ? JSON.parse(experience.skills) : [],
            add_experience: experience.add_experience || "",
          },
        ]
      : [
          {
            id: null,
            company_name: "",
            role: "",
            work_type: "",
            location: "",
            start_date: "",
            end_date: "",
            currently_working: false,
            skills: [],
            add_experience: "",
          },
        ],
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setErrorMessage(null);
      const payload = {
        userId: searchParams.get("id"),
        experience: values.experience.map((exp) => ({
          ...exp,
          start_date: new Date(exp.start_date).toISOString(),
          end_date: exp.currently_working
            ? null
            : new Date(exp.end_date).toISOString(),
        })),
        id: values.experience[0].id,
      };

      if (title === "Add Experience") {
        const res = await dispatch(
          instructorAddExperienceAPIForEditProfile({
            payload,
            token: auth.token,
            role: auth.role,
          })
        ).unwrap();
        if (res.success) {
          toast.success(res.message, { position: "top-center" });
          refreshDataHandler();
          handleClose();
        }
      } else if (title === "Edit Experience") {
        const res = await dispatch(
          instructorUpdateExperienceAPIForEditProfile({
            payload,
            ...payload.id,
            token: auth.token,
            role: auth.role,
          })
        ).unwrap();
        if (res.success) {
          toast.success(res.message, { position: "top-center" });
          refreshDataHandler();
          handleClose();
        }
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to submit experience details";
      console.error("Submission error:", errorMsg);
      toast.error(errorMsg, { position: "top-center" });
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (index, remove, setSubmitting) => {
    try {
      setSubmitting(true);
      setErrorMessage(null);
      remove(index);
    } catch (error) {
      const errorMsg = error.message || "Failed to delete experience entry";
      console.error("Delete error:", errorMsg);
      setErrorMessage(errorMsg);
      toast.error(errorMsg, { position: "top-center" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue, setSubmitting }) => {
          const handleAddSkill = (
            index,
            skill,
            setFieldValue,
            setSkillInput
          ) => {
            if (skill.trim() && skill.length >= 3) {
              setFieldValue(`experience[${index}].skills`, [
                ...values.experience[index].skills,
                skill.trim(),
              ]);
              setSkillInput("");
            }
          };
          return (
            <Form>
              {errorMessage && (
                <div className="text-danger mb-12">{errorMessage}</div>
              )}
              <FieldArray name="experience">
                {({ push, remove }) => (
                  <>
                    {values.experience.map((exp, index) => (
                      <div key={index} className="education-block mb-20">
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="mb-12 mb-md-16">
                              <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                                Company Name
                              </label>
                              <Field
                                name={`experience[${index}].company_name`}
                                className="form-control"
                                placeholder="Enter company name"
                              />
                              <ErrorMessage
                                name={`experience[${index}].company_name`}
                                component="div"
                                className="text-danger error"
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="mb-12 mb-md-16">
                              <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                                Role
                              </label>
                              <Field
                                name={`experience[${index}].role`}
                                className="form-control"
                                placeholder="Enter your role"
                              />
                              <ErrorMessage
                                name={`experience[${index}].role`}
                                component="div"
                                className="text-danger error"
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="mb-12 mb-md-16">
                              <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                                Work Type
                              </label>
                              <Field
                                as="select"
                                name={`experience[${index}].work_type`}
                                className="form-control"
                              >
                                <option value="">Select work type</option>
                                {workTypeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage
                                name={`experience[${index}].work_type`}
                                component="div"
                                className="text-danger error"
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <PlaceAutocomplete
                              index={index}
                              setFieldValue={setFieldValue}
                              values={values}
                            />
                          </div>
                          <div className="col-12 col-md-6 mb-12 mb-md-16">
                            <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                              Start Date
                            </label>
                            <DatePicker
                              selected={
                                values.experience[index].start_date
                                  ? new Date(
                                      values.experience[index].start_date
                                    )
                                  : null
                              }
                              onChange={(date) =>
                                setFieldValue(
                                  `experience[${index}].start_date`,
                                  date ? format(date, "MM-dd-yyyy") : ""
                                )
                              }
                              maxDate={today}
                              dateFormat="MM-dd-yyyy"
                              className="form-control"
                              placeholderText="MM-DD-YYYY"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              onKeyDown={(e) => e.preventDefault()}
                            />
                            <ErrorMessage
                              name={`experience[${index}].start_date`}
                              component="div"
                              className="text-danger error"
                            />
                          </div>
                          <div className="col-12 col-md-6 mb-12 mb-md-16">
                            <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                              End Date
                            </label>
                            <DatePicker
                              selected={
                                values.experience[index].end_date
                                  ? new Date(values.experience[index].end_date)
                                  : null
                              }
                              onChange={(date) =>
                                setFieldValue(
                                  `experience[${index}].end_date`,
                                  date ? format(date, "MM-dd-yyyy") : ""
                                )
                              }
                              minDate={
                                values.experience[index].start_date
                                  ? new Date(
                                      values.experience[index].start_date
                                    )
                                  : null
                              }
                              maxDate={today}
                              dateFormat="MM-dd-yyyy"
                              className="form-control"
                              placeholderText="MM-DD-YYYY"
                              showYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                              disabled={exp.currently_working}
                              onKeyDown={(e) => e.preventDefault()}
                            />
                            <ErrorMessage
                              name={`experience[${index}].end_date`}
                              component="div"
                              className="text-danger error"
                            />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-12 mb-md-16">
                          <Field
                            type="checkbox"
                            id={`experience[${index}].currently_working`}
                            name={`experience[${index}].currently_working`}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded custom-checkbox"
                            onChange={(e) => {
                              setFieldValue(
                                `experience[${index}].currently_working`,
                                e.target.checked
                              );
                              if (e.target.checked) {
                                setFieldValue(
                                  `experience[${index}].end_date`,
                                  ""
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor={`experience[${index}].currently_working`}
                            className="ml-2 text-md text-gray-700 cursor-pointer"
                          >
                            Currently Working
                          </label>
                        </div>

                        <div className="mb-12 mb-md-16">
                          <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                            Skills
                          </label>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter a skill and press Enter"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddSkill(
                                    index,
                                    e.target.value,
                                    setFieldValue,
                                    (value) => (e.target.value = value)
                                  );
                                }
                              }}
                            />
                          </div>
                          {values.experience[index]?.skills?.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mb-2 badge-list mt-10">
                              {values.experience[index].skills.map(
                                (skill, skillIndex) => (
                                  <span
                                    key={skillIndex}
                                    className="badge badge-list-item text-white d-flex align-items-center gap-1"
                                  >
                                    {skill}
                                    <button
                                      type="button"
                                      className="p-0 m-0 bg-transparent border-0"
                                      onClick={() => {
                                        const updatedSkills = values.experience[
                                          index
                                        ].skills.filter(
                                          (_, i) => i !== skillIndex
                                        );
                                        setFieldValue(
                                          `experience[${index}].skills`,
                                          updatedSkills
                                        );
                                      }}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M12 4L4 12M4 4L12 12"
                                          stroke="white"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </button>
                                  </span>
                                )
                              )}
                            </div>
                          )}
                          <ErrorMessage
                            name={`experience[${index}].skills`}
                            component="div"
                            className="text-danger error"
                          />
                        </div>

                        <div className="mb-12 mb-md-16">
                          <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                            About Experience
                          </label>
                          <Field
                            as="textarea"
                            name={`experience[${index}].add_experience`}
                            className="form-control"
                            placeholder="Describe your experience"
                            rows="4"
                          />
                          <ErrorMessage
                            name={`experience[${index}].add_experience`}
                            component="div"
                            className="text-danger error"
                          />
                        </div>

                        {values.experience.length > 1 && (
                          <div className="d-flex justify-content-end mb-12">
                            <button
                              type="button"
                              className="btn btn-danger btn-sm no-shadow min-w-auto border-0 py-7 px-16"
                              onClick={() =>
                                handleDelete(index, remove, setSubmitting)
                              }
                              disabled={isSubmitting}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    <div className="d-flex mb-20">
                      <button
                        type="button"
                        className="add-btn p-0 bg-transparent border-0 d-flex align-items-center gap-2 text-base"
                        onClick={() =>
                          push({
                            id: null,
                            company_name: "",
                            role: "",
                            work_type: "",
                            location: "",
                            start_date: "",
                            end_date: "",
                            currently_working: false,
                            skills: [],
                          })
                        }
                        disabled={isSubmitting}
                      >
                        <svg
                          width="24"
                          height="25"
                          viewBox="0 0 24 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.75"
                            y="0.957947"
                            width="22.5"
                            height="22.5"
                            rx="5.25"
                            fill="#F05203"
                            fillOpacity="0.04"
                          />
                          <rect
                            x="0.75"
                            y="0.957947"
                            width="22.5"
                            height="22.5"
                            rx="5.25"
                            stroke="#F05203"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M6 12.2079L18 12.2079"
                            stroke="#F05203"
                            strokeWidth="2.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 6.20795L12 18.2079"
                            stroke="#F05203"
                            strokeWidth="2.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Add Another Experience
                      </button>
                    </div>
                  </>
                )}
              </FieldArray>
              <div className="pt-12 mb-18 mb-md-24">
                <CustomButton
                  type="submit"
                  label={title === "Edit Experience" ? "Update" : "Save"}
                  classes="btn btn-primary fw-bold w-100 mb-10"
                  disabled={isSubmitting}
                  isSubmitting={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
